import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Building2, Save, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettings = () => {
  const [settings, setSettings] = useState({
    stripe_enabled: false,
    stripe_api_key: "",
    bank_transfer_enabled: false,
    bank_name: "",
    account_holder: "",
    iban: "",
    swift: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/payment-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching payment settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/payment-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Greiðslustillingar vistaðar!");
      fetchSettings();
    } catch (error) {
      toast.error("Villa við að vista stillingar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <CreditCard className="w-8 h-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight" data-testid="payment-settings-title">
            Greiðslustillingar
          </h1>
        </div>

        {/* Stripe Settings */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-violet-600" />
              <div>
                <h2 className="text-lg font-heading font-semibold">Kortgreiðslur (Stripe)</h2>
                <p className="text-sm text-muted-foreground">Leyfa gestum að greiða með kredit-/debetkortum</p>
              </div>
            </div>
            <Switch
              checked={settings.stripe_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, stripe_enabled: checked })}
              data-testid="stripe-enabled-switch"
            />
          </div>

          {settings.stripe_enabled && (
            <div className="border-t border-neutral-100 pt-4">
              <Label htmlFor="stripe_key" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Stripe API lykill (Secret Key)
              </Label>
              <div className="relative">
                <Input
                  id="stripe_key"
                  type={showKey ? "text" : "password"}
                  value={settings.stripe_api_key || ""}
                  onChange={(e) => setSettings({ ...settings, stripe_api_key: e.target.value })}
                  placeholder="sk_live_... eða sk_test_..."
                  className="px-4 py-2 rounded-lg pr-10"
                  data-testid="stripe-api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Finndu lykilinn á <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline text-primary">dashboard.stripe.com/apikeys</a>
              </p>
            </div>
          )}
        </div>

        {/* Bank Transfer Settings */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-emerald-600" />
              <div>
                <h2 className="text-lg font-heading font-semibold">Millifærsla</h2>
                <p className="text-sm text-muted-foreground">Leyfa gestum að greiða með millifærslu</p>
              </div>
            </div>
            <Switch
              checked={settings.bank_transfer_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, bank_transfer_enabled: checked })}
              data-testid="bank-enabled-switch"
            />
          </div>

          {settings.bank_transfer_enabled && (
            <div className="border-t border-neutral-100 pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bank_name" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Banki
                  </Label>
                  <Input
                    id="bank_name"
                    value={settings.bank_name || ""}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="t.d. Landsbankinn"
                    className="px-4 py-2 rounded-lg"
                    data-testid="bank-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="account_holder" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    Reikningseigandi
                  </Label>
                  <Input
                    id="account_holder"
                    value={settings.account_holder || ""}
                    onChange={(e) => setSettings({ ...settings, account_holder: e.target.value })}
                    placeholder="t.d. Casa Arctic ehf."
                    className="px-4 py-2 rounded-lg"
                    data-testid="account-holder-input"
                  />
                </div>
                <div>
                  <Label htmlFor="iban" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    IBAN
                  </Label>
                  <Input
                    id="iban"
                    value={settings.iban || ""}
                    onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                    placeholder="IS12 3456 7890 1234 5678 90"
                    className="px-4 py-2 rounded-lg"
                    data-testid="iban-input"
                  />
                </div>
                <div>
                  <Label htmlFor="swift" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    SWIFT / BIC
                  </Label>
                  <Input
                    id="swift"
                    value={settings.swift || ""}
                    onChange={(e) => setSettings({ ...settings, swift: e.target.value })}
                    placeholder="LANDESXX"
                    className="px-4 py-2 rounded-lg"
                    data-testid="swift-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary hover:bg-primary-600"
          data-testid="save-payment-settings"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Vista..." : "Vista stillingar"}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default PaymentSettings;
